import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';
import { fetcher } from 'utils/axios';

// If you have a real endpoint, put it in .env like: VITE_DASHBOARD_ENDPOINT=/menu/dashboard
// If empty, we won't call the server at all.
const DASHBOARD_PATH = import.meta.env.VITE_DASHBOARD_ENDPOINT || '';

const MENU_MASTER_KEY = 'menu-master'; // SWR cache key for local UI state only (drawer open/close)

const initialState = {
  isDashboardDrawerOpened: false,
  isComponentDrawerOpened: true
};

// Optional – the template adds this extra item to each group
const staticMenuItem = {
  id: 'invoice1',
  title: 'invoice',
  type: 'item',
  url: '/dashboard/invoice',
  breadcrumbs: false
};

/** Load dashboard/menu data from backend (optional). */
export function useGetMenu() {
  // Only call when you actually have an endpoint
  const key =
    DASHBOARD_PATH
      ? (DASHBOARD_PATH.startsWith('/') ? DASHBOARD_PATH : `/${DASHBOARD_PATH}`)
      : null;

  const { data, isLoading, error, isValidating } = useSWR(key, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(() => {
    // If key is null (no endpoint), return an empty state without doing any request
    if (!key) {
      return {
        menu: undefined,
        menuLoading: false,
        menuError: null,
        menuValidating: false,
        menuEmpty: true
      };
    }

    // Some backends return { dashboard: {...} } and some return the object directly
    let updatedMenu = data?.dashboard ?? data;

    if (updatedMenu && Array.isArray(updatedMenu.children) && updatedMenu.children.length > 0) {
      updatedMenu = {
        ...updatedMenu,
        children: updatedMenu.children.map((group) => {
          if (Array.isArray(group.children)) {
            return {
              ...group,
              children: [...group.children, staticMenuItem]
            };
          }
          return group;
        })
      };
    }

    return {
      menu: updatedMenu,
      menuLoading: isLoading,
      menuError: error,
      menuValidating: isValidating,
      menuEmpty: !isLoading && !updatedMenu
    };
  }, [key, data, error, isLoading, isValidating]);

  return memoizedValue;
}

/** Local UI state for the dashboard drawers (no server). */
export function useGetMenuMaster() {
  const { data, isLoading } = useSWR(MENU_MASTER_KEY, () => initialState, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return useMemo(
    () => ({
      menuMaster: data || initialState,
      menuMasterLoading: isLoading
    }),
    [data, isLoading]
  );
}

export function handlerComponentDrawer(isComponentDrawerOpened) {
  mutate(
    MENU_MASTER_KEY,
    (current = initialState) => ({ ...current, isComponentDrawerOpened }),
    false
  );
}

export function handlerDrawerOpen(isDashboardDrawerOpened) {
  mutate(
    MENU_MASTER_KEY,
    (current = initialState) => ({ ...current, isDashboardDrawerOpened }),
    false
  );
}
